import { useMemo } from 'react';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { DeviceBreakdown } from '../../types/analytics';
import { pivotDevices } from '../../utils/analyticsTransformers';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

interface DeviceBreakdownChartProps {
  devices: DeviceBreakdown[];
  dimension: string;
  title: string;
}

export default function DeviceBreakdownChart({ devices, dimension, title }: DeviceBreakdownChartProps) {
  const data = useMemo(() => pivotDevices(devices, dimension), [devices, dimension]);

  if (data.length === 0) {
    return (
      <Card className="bg-admin-dark-800 border-admin-slate-300/10">
        <CardHeader><CardTitle className="text-heading-5 text-admin-slate-400">{title}</CardTitle></CardHeader>
        <CardContent><p className="text-admin-slate-500 text-sm">No device data available.</p></CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-admin-dark-800 border-admin-slate-300/10">
      <CardHeader>
        <CardTitle className="text-heading-5 text-admin-slate-400">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={100}
              paddingAngle={2}
              label={({ name, percent }: { name?: string; percent?: number }) => name && percent != null ? `${name} (${(percent * 100).toFixed(0)}%)` : ''}
            >
              {data.map((_, index) => (
                <Cell key={index} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 8 }}
              itemStyle={{ color: '#94a3b8' }}
            />
            <Legend wrapperStyle={{ color: '#94a3b8' }} />
          </PieChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
